/* eslint-disable max-len */
import { AppAPI } from '@moderepo/mode-apis';
import { convertCollectionDataToMetricsDataPoints, DataPoint, MemorySize, mergeMetricDataToMultiMetricDataPoints, MultiSeriesDataPoint, TimeSpan }
    from '@moderepo/mode-app-components';
import { selectEntityById, setEntity, UserAppDataState, UserAppDataStateAction, batchActions, ExtDispatch, SetEntityAction }
    from '@moderepo/mode-data-state-user-app';
import { AttributeValueCompareOperator, Entity, EntityAttributeParam, EntityAttributeType, EntityAttributeValueType, EntityClass,
    EntityMetricsDefinition, EntityNumericMetricAlertCondition, EvaluationPeriod, NumericMetricInfo, ThresholdComparisonOperator,
    TimeSeriesAggregation, TimeSeriesResolution, AttributeConditionConjunction, AlertSeverity, EntitySortFieldType } from '@moderepo/mode-internal-apis';
import { TFunction } from 'i18next';
import moment from 'moment';
import { useMemo } from 'react';
import { filterMetricsByMetricsExclusionCustomSettings, MetricsExclusionCustomSettings } from '.';
import { EntityInfoUpdatableInputs } from '../../components';
import { useSelectEntityClasses } from '../../utils/dataStateHooks';
import { genericValidateArrayInput, genericValidateBooleanInput, genericValidateNumberInput, genericValidateObjectInput, genericValidateStringInput,
    isValidSettingStringValue, NonEmptyArray, ReadonlyNonEmptyArray } from '../../utils/utils';
import { SettingsErrorLogger } from '../../utils/logger';


// Root entities don't have parentId and we will be using the string 'root' as parent ID for these entities. When we request for top level
// entities, we have to pass this string 'root' as entityParentId because backend will use this same string to determine if the frontend
// want to fetch root entities or entities belonged to another parent entity.
export const ROOT_ENTITY_PARENT_ID = 'root';


// eslint-disable-next-line no-useless-escape
export const ENTITY_ID_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9_\-]+[a-zA-Z0-9]$/;


// Default # of timestamp inputs we will show the user
export const DEFAULT_MIN_TIMESTAMP_INPUTS = 1;

// Default maximum # of timestamp inputs we will allow the user to add
export const DEFAULT_MAX_TIMESTAMP_INPUTS = 2;

// The default first day of the week is SUNDAY. The value 0 is from Javascript Date.getDay() which return value 0-6 for Sun to Sat
export const DEFAULT_FIRST_DAY_OF_WEEK = 0;

// Whether or not to optimize the date selector and only allow the user to input year, month, or day instead of all
export const OPTIMIZE_DATE_SELECTOR = true;

// The day of week in numeric form. This is based on the value from Javascript's day of week. SUNDAY = 0, MONDAY = 1, TUESDAY = 2, etc...
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export enum KPIStatus {
    NONE = 'none',
    NEUTRAL = 'neutral',
    GOOD = 'good',
    BAD = 'bad',
}

// The default view displayName if a name is not provided
export const DEFAULT_ENTITY_VIEW_DISPLAY_NAME_KEY = 'label.report';


// Set the upload file size a maximum to 1MB for icon and 10MB for image to prevent the user from selecting a very large image file
export const MAX_ENTITY_ICON_UPLOAD_FILE_SIZE = MemorySize.MEGABYTE * 1;        // Icons should be smaller than Images
export const MAX_ENTITY_IMAGE_UPLOAD_FILE_SIZE = MemorySize.MEGABYTE * 10;


// Special attribute keys that are used for mapping an entity to MODE objects e.g. users, homes, devices
export const ENTITY_USER_ID_ATTRIBUTE = '_userId_';
export const ENTITY_HOME_ID_ATTRIBUTE = '_homeId_';
export const ENTITY_DEVICE_ID_ATTRIBUTE = '_deviceId_';


// The list of entity BASIC attributes that are sortable
export const SORTABLE_BASIC_ENTITY_ATTRIBUTES: readonly string[] = ['name', 'homeId', 'entityId', 'createdAt', 'updatedAt'];

// The list of entity CUSTOM attribute types that are sortable
export const SORTABLE_CUSTOM_ATTRIBUTE_TYPES: readonly string[] = [EntityAttributeType.STRING, EntityAttributeType.NUMBER, EntityAttributeType.BOOLEAN];



// Entities returned by the backend are array of entities but they actually has a tree like structure. The frontend will need to use
// a tree to represent them therefore we will create this interface to wrap each entity object. The entity tree will be consists of
// entity nodes.
export interface EntityNode {
    readonly entity: Entity;

    readonly disabled?: boolean | undefined;

    // When children's value is 'undefined' it DOES NOT mean the node does not have children. If just means we DON'T know yet
    // whether the node has children. If the value is an EMPTY array then that mean the node DOES NOT have children.
    readonly children: readonly EntityNode[];

    // A flag to indicate whether the entity has more children to be loaded. We need this flag so that we know whether we need to
    // make API calls to fetch more entities when a node is selected.
    readonly hasMoreChildren: boolean;

    // A flag to keep track if we have already fetched data for the current pages. For example, if the user is on page # 2, we will
    // need to call fetch entities API twice to fetch the entities for those 2 pages so we use this flags to determine if we need
    // to fetch more pages
    readonly numberOfPagesFetched: number;
}


/**
 * Given a Tree rootNode and an entityId, find a node from the tree that contain the entity with the specified entityId
 */
export const findEntityNode = (rootEntityNode: EntityNode | undefined, entityId: string): EntityNode | undefined => {
    if (entityId && rootEntityNode) {
        if (rootEntityNode.entity.entityId === entityId) {
            return rootEntityNode;
        }
        if (rootEntityNode.children) {
            // eslint-disable-next-line no-restricted-syntax
            for (const child of rootEntityNode.children) {
                const result = findEntityNode(child, entityId);
                if (result) {
                    return result;
                }
            }
        }
    }

    return undefined;
};



/**
 * This interface is used for storing groups of KPI metrics that has all the params we need to fetch data.
 *
 * Each Metric definition has an array of numeric/tag metrics and because they all belonged to the same collection we can fetch data for
 * all these metrics together in 1 call. However, because KPI metrics in the same metrics definition can have different aggregation, therefore
 * we can't always fetch all the KPI metrics in the same collection with 1 call. 1 solution is to fetch 1 metric at a time but that mean
 * there will be a lot of API calls. So to optimize it, we will need to group KPI metrics into groups where each group contain a list of
 * KPI metrics that belonged to the same metric definition AND have the same aggregation type. So 2 KPI metrics that belonged in the same
 * metric definition but have different aggregation type will be in different group.
 */
export interface FetchEntityMetricDataParamsGroup {
    // A unique ID for a group. This key will be a combination of collectionId + aggregation. Any metric that have the same
    // key can be in the same group and can be fetched in the same call because having the same 'collectionId' means they belonged in
    // the same collection and if they also have the same 'aggregation' then they can be fetched together.
    readonly groupKey: string;

    // Other attributes needed for making API call
    readonly entity: Entity;
    readonly metricDefinition: EntityMetricsDefinition;

    // Even though we need aggregation for grouping BUT it is not required because the default is AVERAGE. So all KPI metrics that DON'T
    // have aggregation can be in the same group.
    readonly aggregation?: TimeSeriesAggregation | undefined;

    // The list of metrics
    readonly metrics: NumericMetricInfo[];
}



/**
 * A helper function to check if an entity is a root entity. An entity is a root entity if it has no parentId or the parentId === 'root'.
 * Backend might always return 'root' for parentId but we will not going to assume that just incase. We will check both.
 */
export const isRootEntity = (entity: Entity) => {
    return entity.parentId === undefined || entity.parentId === ROOT_ENTITY_PARENT_ID;
};


/**
 * Helper function to generate metricsId for the specified metrics definition. This ID will be used for fetching data. For TSDB, this will be
 * the collectionId. For 'home_app_proxy', this sill be the searchKey, etc...
 */
export const getMetricsDefinitionMetricsId = (entity: Entity, metricsDefinitionId: string): string => {
    const metricsInfo = entity.metrics?.find((metric) => {
        return metric.metricsDefinitionId === metricsDefinitionId;
    });

    // If we can fine metrics info from the entity instance, use it. If not, use the collectionId template.
    return metricsInfo?.metricsId ?? `${entity.entityClass}::${entity.entityId}::${metricsDefinitionId}`;
};



export interface EntityInstanceInputErrors {
    homeId?: string | undefined;
    entityId?: string | undefined;
    entityClass?: string | undefined;
    parent?: string | undefined;
    name?: string | undefined;
    description?: string | undefined;
    iconUrl?: string | undefined;
    imageUrl?: string | undefined;
    timeZone?: string | undefined;
    attributes?: {
        [key: string]: string | undefined;
    } | undefined;
}


/**
 * Validate the inputs for entity ATTRIBUTES
 */
const validateEntityAttributeInputs = (
    inputs: EntityInfoUpdatableInputs,
    attributeSchema: readonly EntityAttributeParam<EntityAttributeValueType>[],
    isNewInputs: boolean,
    trans: TFunction,
): EntityInstanceInputErrors['attributes'] | undefined => {
    const { attributes } = inputs;
    const attributeInputErrors = {};

    attributeSchema.forEach((attributeConfig) => {
        const key = attributeConfig.key;
        const value = attributes?.[key];
        if (attributeConfig.type === EntityAttributeType.NUMBER) {
            attributeInputErrors[key] = genericValidateNumberInput(value, attributeConfig, isNewInputs, trans);
        } else if (attributeConfig.type === EntityAttributeType.STRING) {
            attributeInputErrors[key] = genericValidateStringInput(value, attributeConfig, isNewInputs, trans);
        } else if (attributeConfig.type === EntityAttributeType.BOOLEAN) {
            attributeInputErrors[key] = genericValidateBooleanInput(value, attributeConfig, trans);
        } else if (attributeConfig.type === EntityAttributeType.ARRAY) {
            attributeInputErrors[key] = genericValidateArrayInput(value, attributeConfig, trans);
        } else if (attributeConfig.type === EntityAttributeType.OBJECT) {
            attributeInputErrors[key] = genericValidateObjectInput(value, attributeConfig, trans);
        }
    });

    const hasErrors = Object.values(attributeInputErrors).some((error) => {
        return error !== undefined;
    });

    // Return undefined if is are no errors, don't return empty {}
    return hasErrors ? attributeInputErrors : undefined;
};


/**
 * Validate the inputs for UPDATE entity inputs which only 'name' is required and need to validate
 */
export const validateUpdateEntityInstanceInputs = (
    inputs: EntityInfoUpdatableInputs,
    entity: Entity,
    attributeSchema: readonly EntityAttributeParam<EntityAttributeValueType>[] | undefined,
    trans: TFunction,
): EntityInstanceInputErrors => {
    const inputErrors: EntityInstanceInputErrors = {};

    const { parent, name, iconUrl, imageUrl } = inputs;

    // parent is optional but if it is provided, make sure it is not the same as the entity.entityId because that would
    // make the entity it's own parent.
    if (parent !== undefined && parent.entityId === entity.entityId) {
        inputErrors.parent = 'error.input.generic.invalid';
    }

    if (!name) {
        inputErrors.name = 'error.input.generic.empty';
    }

    if (iconUrl && iconUrl.size > MAX_ENTITY_ICON_UPLOAD_FILE_SIZE) {
        inputErrors.iconUrl = 'input.image.max_size_error';
    }

    if (imageUrl && imageUrl.size > MAX_ENTITY_IMAGE_UPLOAD_FILE_SIZE) {
        inputErrors.imageUrl = 'input.image.max_size_error';
    }

    // If entityClass has attributeSchemas then call 'validateEntityAttributeInputs' to validate attribute inputs
    if (attributeSchema && attributeSchema?.length > 0) {
        inputErrors.attributes = validateEntityAttributeInputs(inputs, attributeSchema, false, trans);
    }

    return inputErrors;
};


/**
 * Validate the inputs for NEW entity inputs
 */
export const validateNewEntityInstanceInputs = (
    inputs: EntityInfoUpdatableInputs,
    attributeSchema: readonly EntityAttributeParam<EntityAttributeValueType>[] | undefined,
    trans: TFunction,
): EntityInstanceInputErrors => {
    const inputErrors: EntityInstanceInputErrors = {};

    const { entityClass, entityId, homeId, name, timeZone } = inputs;

    if (!homeId) {
        inputErrors.homeId = 'error.input.generic.empty';
    } else if (Number.isNaN(Number(homeId))) {
        inputErrors.homeId = 'error.input.generic.invalid';
    }

    if (!entityClass) {
        inputErrors.entityClass = 'error.input.generic.empty';
    }

    if (!entityId) {
        inputErrors.entityId = 'error.input.generic.empty';
    } else if (!ENTITY_ID_REGEX.test(entityId)) {
        inputErrors.entityId = 'error.input.generic.invalid';
    }

    if (!name) {
        inputErrors.name = 'error.input.generic.empty';
    }

    if (!timeZone) {
        inputErrors.timeZone = 'error.input.generic.empty';
    }

    // If entityClass has attributeSchemas then call 'validateEntityAttributeInputs' to validate attribute inputs
    if (attributeSchema && attributeSchema?.length > 0) {
        inputErrors.attributes = validateEntityAttributeInputs(inputs, attributeSchema, true, trans);
    }

    return inputErrors;
};



/**
 * The data structure that contain information for a metric that we can use to display a preview of a metric
 */
export interface MetricPreviewData {
    readonly metricDefinition: EntityMetricsDefinition;
    readonly metric: NumericMetricInfo;
    readonly dataPoints: readonly MultiSeriesDataPoint<number>[];
    readonly status: KPIStatus,
    readonly currentValue: number | undefined;
    readonly previousValue: number | undefined;
}


/**
 * 'lessThan', 'lessThanOrEqualTo', 'greaterThanOrEqualTo', or 'greaterThan' only need to compare value to 1 threshold
 */
const isValueLessThanThreshold = (value: number, thresholds: readonly number[]): boolean => {
    return thresholds.length >= 1 && value < thresholds[0];
};
const isValueLessThanOrEqualToThreshold = (value: number, thresholds: readonly number[]): boolean => {
    return thresholds.length >= 1 && value <= thresholds[0];
};
const isValueGreaterThanThreshold = (value: number, thresholds: readonly number[]): boolean => {
    return thresholds.length >= 1 && value > thresholds[0];
};
const isValueGreaterThanOrEqualToThreshold = (value: number, thresholds: readonly number[]): boolean => {
    return thresholds.length >= 1 && value >= thresholds[0];
};


/**
 * Check if the value is 'IN' one of the thresholds. This means if the value is equal to 1 of the thresholds then return true.
 */
const isValueInThresholds = (value: number, thresholds: readonly number[]): boolean => {
    return thresholds.includes(value);
};

/**
 * Check if the value IS NOT in any of the thresholds. This means if the value DOES NOT exist in the thresholds then return true
 */
const isValueNotInThresholds = (value: number, thresholds: readonly number[]): boolean => {
    // IMPORTANT NOTE: Need to check if threshold.length > 0 because if thresholds.length === 0 then this will already return true
    // which might not be correct. If there is no thresholds to compare to then the condition should be false.
    return thresholds.length > 0 && !thresholds.includes(value);
};

/**
 * Check if the value is BETWEEN 2 thresholds, threshold[0] <= value <= thresholds[1]
 */
const isValueBetweenThresholds = (value: number, thresholds: readonly number[]): boolean => {
    return thresholds.length >= 2 && value >= thresholds[0] && value <= thresholds[1];
};

/**
 * Check if the value is OUTSIDE 2 thresholds, value < thresholds[0] OR value > thresholds[1], the opposite of BETWEEN
 */
const isValueOutsideThresholds = (value: number, thresholds: readonly number[]): boolean => {
    // IMPORTANT NOTE: Need to check thresholds.length >= 2 because if we don't check this and if thresholds.length < 2,
    // isValueBetweenThresholds will return FALSE and because this returns !false therefore it returns TRUE which is NOT correct.
    // If thresholds.length < 2 then the condition should return false.
    return thresholds.length >= 2 && !isValueBetweenThresholds(value, thresholds);
};


/**
 * Evaluate a given alert condition and return true if the condition is met and false otherwise
 * @param alertCondition
 * @returns
 */
const evaluateAlertCondition = (value: number, alertCondition: EntityNumericMetricAlertCondition): boolean => {
    switch (alertCondition.operator) {
        case ThresholdComparisonOperator.LESS_THAN:
            return isValueLessThanThreshold(value, alertCondition.thresholds);
        case ThresholdComparisonOperator.LESS_THAN_OR_EQUAL_TO:
            return isValueLessThanOrEqualToThreshold(value, alertCondition.thresholds);
        case ThresholdComparisonOperator.GREATER_THAN:
            return isValueGreaterThanThreshold(value, alertCondition.thresholds);
        case ThresholdComparisonOperator.GREATER_THAN_OR_EQUAL_TO:
            return isValueGreaterThanOrEqualToThreshold(value, alertCondition.thresholds);
        case ThresholdComparisonOperator.IN:
            return isValueInThresholds(value, alertCondition.thresholds);
        case ThresholdComparisonOperator.NOT_IN:
            return isValueNotInThresholds(value, alertCondition.thresholds);
        case ThresholdComparisonOperator.BETWEEN:
            return isValueBetweenThresholds(value, alertCondition.thresholds);
        case ThresholdComparisonOperator.OUTSIDE:
            return isValueOutsideThresholds(value, alertCondition.thresholds);
        default:
            return false;
    }
};



/**
 * A convenience function to check if a Metric is a KPI metric. Even though this function is simple and it only check if
 * metricInfo.evaluator !== undefined, we should use this function to check if a metric is a KPI metric instead of
 * doing this 'metricInfo.evaluator !== undefined'. Because if we change the logic and need to add other check, we only need to
 * update this function.
 */
export const isKPIMetric = (metricInfo: NumericMetricInfo | undefined): boolean => {
    return metricInfo?.evaluator !== undefined;
};



/**
 * Given an EntityClass, get all the KPI metric definitions from the entity class, the metric definition that has at least 1 KPI
 * numeric metric. All the metrics definition that DOES NOT have at least 1 KPI metrics will be excluded.
 */
export const getKPIMetricDefinitions = (entityClass: EntityClass | undefined): readonly EntityMetricsDefinition[] | undefined => {
    if (entityClass) {
        // Find all KPI metric definitions
        return entityClass.metricsDefinitions?.filter((metricDef) => {
            // Find a KPI numeric metric, a numeric metric that has 'evaluator' settings
            const numericKpiMetric = metricDef.numericMetrics?.find((metric) => {
                return isKPIMetric(metric);
            });

            // return true if there is 1 KPI numeric metric
            return numericKpiMetric !== undefined;
        });
    }
    return undefined;
};



/**
 * Go through the metricDefinition's numericMetrics array and get the list of KPI numeric metrics, the numeric metrics that has 'evaluator'
 * attribute.
 */
export const getKPIMetricsFromMetricDefinition = (metricDef: EntityMetricsDefinition | undefined) => {
    if (metricDef) {
        // Find all the numericMetrics that are KPI metrics
        const result = metricDef.numericMetrics?.filter((numericMetric) => {
            return isKPIMetric(numericMetric);
        });

        if (result && result.length > 0) {
            // Because KPI metric has its own displayName and we want to use that name instead of the metric.displayName attribute, we have to
            // make copies of the result and replace the metric.displayName with the metric.evaluator.displayName
            return result.map((numericMetric) => {
                return {
                    ...numericMetric,
                    displayName: numericMetric.evaluator?.displayName ?? numericMetric.name,
                };
            });
        }
    }
    return undefined;
};


/**
 * Given an entity class, find and return ALL KPI metrics in the class.
 */
export const getKPIMetricsFromEntityClass = (entityClass: EntityClass | undefined) => {
    const kpiMetricDefinitions = getKPIMetricDefinitions(entityClass);
    if (kpiMetricDefinitions) {
        // For each KPI metric definition, find all the KPI metrics
        return kpiMetricDefinitions.map((metricDef) => {
            // Find all the numericMetrics that are KPI metric
            const result = metricDef.numericMetrics?.filter((numericMetric) => {
                return isKPIMetric(numericMetric);
            });
            if (result && result.length > 0) {
                // Because KPI metric has its own displayName and we want to use that name instead of the metric.displayName attribute, we have to
                // make copies of the result and replace the metric.displayName with the metric.evaluator.displayName
                return result.map((numericMetric) => {
                    return {
                        ...numericMetric,
                        displayName: numericMetric.evaluator?.displayName ?? numericMetric.name,
                    };
                });
            }

            return result;
        }).flat();
    }
    return undefined;
};


/**
 * Calculate the KPI status for 1 metric. We will use the metric data and metric.evaluator to compute the status of the metric
 */
export const getMetricKPIStatus = (value: number | undefined, metric: NumericMetricInfo, timeSpan: TimeSpan): KPIStatus => {

    // Not all KPI metric has alertCondition/s. Only continue if the metric has at least 1 condition.
    if (value !== undefined && metric.evaluator && metric.evaluator.alertConditions && metric.evaluator.alertConditions.length > 0) {

        // metric.evaluator.alertConditions has 1 or more conditions but we don't need to evaluate all. We only need to evaluate the
        // condition/s configured for the given timeSpan. So we need to filter out conditions that are NOT configured for the given timeSpan
        const conditionsToEvaluate = metric.evaluator.alertConditions.filter((con) => {
            return (timeSpan === TimeSpan.DAY && con.period === EvaluationPeriod.DAILY)
                || (timeSpan === TimeSpan.WEEK && con.period === EvaluationPeriod.WEEKLY)
            || (timeSpan === TimeSpan.MONTH && con.period === EvaluationPeriod.MONTHLY);
        });

        if (conditionsToEvaluate.length > 0) {
            // Loop through the array of alertConditions and find 1 condition that evaluated successfully
            const successCondition = conditionsToEvaluate.find((condition) => {
                const success = evaluateAlertCondition(value, condition);
                if (success) {
                    return true;
                }
                return false;
            });

            // If there is at least 1 condition evaluated successfully then that mean the metric's current value
            // reached the threshold therefore it is in a BAD state so we return BAD status.
            if (successCondition) {
                return KPIStatus.BAD;
            }

            // None of the conditions evaluated successfully then the metric is GOOD.
            return KPIStatus.GOOD;
        }
    }

    return KPIStatus.NEUTRAL;
};



/**
 * This is a helper function to get all the necessary data for a group of metrics in order to display a preview of the metric
 */
export const getEntityMetricGroupPreviewData = async (
    metricGroup: FetchEntityMetricDataParamsGroup,
    startTimestamp: number,
    timeSpan: TimeSpan,
    includeCurrentValue: boolean = true,
    includeHistoricalData: boolean = false,
    includePreviousData: boolean = false,
): Promise<MetricPreviewData[]> => {
    const collectionId = getMetricsDefinitionMetricsId(metricGroup.entity, metricGroup.metricDefinition.metricsDefinitionId);
    const startTime = moment(startTimestamp).startOf(timeSpan);
    const endTime = moment(startTimestamp).endOf(timeSpan);

    // If we need to fetch data for the previous time span, e.g. yesterday, last week, last month, then set the startTime to 1 time span earlier
    const previousTimestamp = moment(startTimestamp).subtract(1, timeSpan).startOf(timeSpan);

    // Get the valueNames for the array of metrics in the group
    const valueNames = metricGroup.metrics.map((metric) => {
        return metric.name;
    });


    // Fetch time series data for the metrics in the group
    const collectionData = includeHistoricalData ? await AppAPI.getInstance().getTimeSeriesCollectionData(
        metricGroup.entity.homeId, metricGroup.metricDefinition.dataStoreConfiguration.moduleInstanceId, collectionId,
        startTime.toISOString(), endTime.toISOString(), valueNames, metricGroup.aggregation,
    ) : undefined;

    // Convert data to another structure that can be used by charts
    const dataPoints = includeHistoricalData && collectionData
        ? mergeMetricDataToMultiMetricDataPoints(convertCollectionDataToMetricsDataPoints(collectionData, valueNames).numericMetrics)
        : [];

    // Get the aggregated data based on the timeSpan and timestamp for all the metrics in the metricGroup. This will be a
    // double array of dataPoints, 1 per metric.
    // Because this will fetch the data used for displaying the currentValue and previous value for comparison, we will need to use
    // the previousTimestamp if "includePreviousData" is set to true.
    const multiMetricsAggregatedValues = includeCurrentValue
        ? await getAllMetricsAggregatedValues(metricGroup, includePreviousData ? previousTimestamp.valueOf() : startTime.valueOf(), endTime.valueOf(), timeSpan)
        : undefined;

    // Return all the data needed to display Entity preview, one per metric
    return metricGroup.metrics.map((metric, metricIndex) => {

        // getMetricAggregatedValues returns an ARRAY of data points and it is possible that it return no data points so we
        // need to call 'getAggregatedValueBetweenTimestamps' to find and return the data point between the start/end time
        const metricCurrentValue = includeCurrentValue
            ? getAggregatedValueBetweenTimestamps(
                multiMetricsAggregatedValues?.[metricIndex], startTime.valueOf(), endTime.valueOf(),
            )
            : undefined;

        const metricPreviousValue = includePreviousData
            ? getAggregatedValueBetweenTimestamps(
                multiMetricsAggregatedValues?.[metricIndex], previousTimestamp.valueOf(), moment(previousTimestamp).endOf(timeSpan).valueOf(),
            )
            : undefined;

        // Get the KPI metric's status based on the collectionData and the metric's alertCondition
        const metricKPIStatus = isKPIMetric(metric) ? getMetricKPIStatus(metricCurrentValue, metric, timeSpan) : KPIStatus.NONE;

        return {
            metricDefinition: metricGroup.metricDefinition,
            metric,
            dataPoints,
            status          : metricKPIStatus,
            currentValue    : metricCurrentValue,
            previousValue   : metricPreviousValue,
        };
    });
};



/**
 * Given an array of metric definition, return an array of metric groups for which each group of metrics can be fetched in the same API call.
 *
 * For example:
 *
 * Input: Given 2 metric definitions
 *      metricDefinitions: [{
 *          id: tyo_environment,
 *          metrics: [temperature (avg), humidity (avg), uptime (sum)]
 *      }, {
 *          id: sfo_environment,
 *          metrics: [temperature (COUNT), humidity (COUNT), pressure (COUNT)]
 *      }]
 *
 * Output: Return 3 groups of FetchEntityMetricDataParams
 *      [{
 *          entity: TYO,
 *          metricDefinition: tyo_environment,
 *          aggregation: "AVG",                 // Because of this aggregation TYO's environment need to be split into 2 groups
 *          metrics: [temperature, humidity]
 *      }, {
 *          entity: TYO
 *          metricDefinition: tyo_environment,
 *          aggregation: "SUM",                 // Because of this aggregation TYO's environment need to be split into 2 groups
 *          metrics: [uptime]
 *      }, {
 *          entity: SFO
 *          metricDefinition: sfo_environment,
 *          aggregation: "COUNT",
 *          metrics: [temperature, humidity, pressure]
 *      }]
 *
 * NOTE: the 'metrics' array in each group is guarantee to have at least 1 metric. We won't create a group without any metric. This is important
 * because backend doesn't allow fetching collection data with valueName === [] so if 'metrics' can be empty then we need to check for empty
 * array when we make API call which can be tedious.
 */
export const createFetchEntityMetricDataParamsGroup = (
    metricDefinitions: readonly EntityMetricsDefinition[],
    entity: Entity,
    kpiMetricsOnly: boolean,
    metricsExclusionSettings?: MetricsExclusionCustomSettings | undefined,
    defaultAggregation?: TimeSeriesAggregation | undefined,
): readonly FetchEntityMetricDataParamsGroup[] => {

    // Create an empty array of groups
    const metricGroups: FetchEntityMetricDataParamsGroup[] = [];

    metricDefinitions.forEach((metricDef) => {

        // Get the collectionId of the collection the metricDef belonged to
        const collectionId = getMetricsDefinitionMetricsId(entity, metricDef.metricsDefinitionId);

        // Get ONLY the KPI metrics in the metricDef excluding the other metrics if "kpiMetricsOnly". Else return all metrics.
        const metrics = kpiMetricsOnly ? metricDef.numericMetrics?.filter((metric) => {
            return isKPIMetric(metric);
        }) : metricDef.numericMetrics;

        // If "metricsExclusionSettings" is provided, filter out metrics based on "metricsExclusionSettings"
        const filteredMetrics = filterMetricsByMetricsExclusionCustomSettings(
            metricDef.metricsDefinitionId, metrics, metricsExclusionSettings, true,
        );

        filteredMetrics.forEach((metric) => {

            // Create a groupKey based on the collectionId + aggregation and then try to find a group in the 'metricGroups' that
            // has the same key. Any metric that has the same key can be in the same group
            const aggregation = metric.evaluator?.aggregation ?? defaultAggregation ?? TimeSeriesAggregation.AVG;
            const groupKey = `${collectionId}-${aggregation}`;
            const existingGroup = metricGroups.find((group) => {
                return group.groupKey === groupKey;
            });

            if (existingGroup) {
                // The current metric belonged to a group that already exist so we can just add the metric to the group's array of metrics
                existingGroup.metrics.push(metric);
            } else {
                // the current metric does not belong to any existing group in the result therefore we will create a new group for the metric
                // and add the group to the result
                const newGroup: FetchEntityMetricDataParamsGroup = {
                    groupKey,
                    entity,
                    metricDefinition: metricDef,
                    aggregation,
                    metrics         : [metric],
                };

                metricGroups.push(newGroup);
            }
        });
    });

    return metricGroups;
};



/**
 * Get the aggregated values for each metric in the metricGroup based on the provided start/end timestamp and timeSpan. For example: If
 * timeSpan is 1 day then we will get the daily aggregated value between start/end time. If the timeSpan is 1 week then we will get weekly
 * aggregated value between start/end time, etc...
 *
 * This will return an array of aggregated value, 1 per metric in the same order as metricGroup.metrics
 */
export const getAllMetricsAggregatedValues = async (
    metricGroup: FetchEntityMetricDataParamsGroup, startTime: number, endTime: number, timeSpan: TimeSpan,
): Promise<readonly DataPoint<number>[][] | undefined> => {
    const resolution = ((): TimeSeriesResolution | undefined => {
        if (timeSpan === TimeSpan.MINUTE) {
            return TimeSeriesResolution.MIN_1;
        }
        if (timeSpan === TimeSpan.HOUR) {
            return TimeSeriesResolution.HOUR_1;
        }
        if (timeSpan === TimeSpan.DAY) {
            return TimeSeriesResolution.DAY_1;
        }
        if (timeSpan === TimeSpan.WEEK) {
            return TimeSeriesResolution.WEEK_1;
        }
        if (timeSpan === TimeSpan.MONTH) {
            return TimeSeriesResolution.MONTH_1;
        }
        // If the timeSpan is none of the above then return undefined and let the backend pick one automatically
        return undefined;
    })();

    const collectionId = getMetricsDefinitionMetricsId(metricGroup.entity, metricGroup.metricDefinition.metricsDefinitionId);
    const valueNames = metricGroup.metrics.map((metric) => {
        return metric.name;
    });
    const collectionData = await AppAPI.getInstance().getTimeSeriesCollectionData(
        metricGroup.entity.homeId, metricGroup.metricDefinition.dataStoreConfiguration.moduleInstanceId,
        collectionId, moment(startTime).toISOString(), moment(endTime).toISOString(), valueNames, metricGroup.aggregation, resolution,
    );

    if (collectionData.data.length > 0) {
        return metricGroup.metrics.map((_, metricIndex) => {
            // Loop through the array of data returned by backend and convert each item to a DataPoint object
            return collectionData.data.map((dataRow) => {

                // The collectionData.data will be a double Array of data points. The first element is the Date string and the following values
                // will be the value for each metric, 1 per column. If we pass N valueNames to the fetch data call then we can expect N + 1 columns
                // of data back
                const date = dataRow[0];
                const value = dataRow[metricIndex + 1];
                const dateObj = moment(date).toDate();

                return {
                    date      : dateObj,
                    dateString: date,
                    dateValue : dateObj.valueOf(),
                    // It is possible that the value is null so need to check for null before cast it to number
                    value     : value !== undefined && value !== null ? value as number : null,
                };
            });
        });
    }

    return undefined;
};



/**
 * Given an array of data points and the start/end time, find a data point within these start/end time and return it's value. Return undefined
 * if can't find any data point within the start/end time
 */
export const getAggregatedValueBetweenTimestamps = (
    dataPoints: readonly DataPoint<number>[] | undefined, startTime: number, endTime: number,
): number | undefined => {
    if (dataPoints && dataPoints.length > 0) {
        // Find the data point which has dateValue within start/end time
        const dataPoint = dataPoints.find((point) => {
            return startTime <= point.dateValue && point.dateValue <= endTime;
        });

        if (dataPoint && dataPoint.value !== null) {
            return dataPoint.value;
        }
    }
    return undefined;
};



/**
 * Compare 2 attributes and return true if they are the same and false otherwise. 2 attributes are the same if they have the same
 * key, type, and options
 */
const compareAttributes = (
    attribute1: EntityAttributeParam<EntityAttributeValueType>,
    attribute2: EntityAttributeParam<EntityAttributeValueType>,
): boolean => {
    if (attribute1.key !== attribute2.key) {
        return false;
    }
    if (attribute1.type !== attribute2.type) {
        return false;
    }
    if (JSON.stringify(attribute1.options) !== JSON.stringify(attribute2.options)) {
        // If the string presentation of the attributes' options are different then we will consider them as different attributes
        return false;
    }
    return true;
};


/**
 * Compile a list of all attributes from ALL entity classes that can be used as filter. If some entityClasses are specified then this will
 * only use the attributes defined in those entityClasses, not all entity classes.
 *
 * NOTE that it is possible for multiple attributes to have the same key therefore we might not need to include all attributes. If 2 attributes
 * have the same key and unit, we can consider them the same attribute therefore we only need to show 1 attribute in the UI. If 2 attributes
 * have the same key but different type them we will include both in the result because the user will need to enter different value for
 * different type.
 *
 *      [
 *          {key:'name', label: 'Employee Name', type: 'string'}
 *          {key:'age', label: 'Employee Age', type: 'number},
 *          {key:'model', label: 'Car Model', type: 'string'}
 *      ]
 */
export const getFilterableAttributes = (
    entityClasses: readonly EntityClass[] | undefined,
    selectedEntityClasses?: readonly string[] | undefined,
): readonly EntityAttributeParam<EntityAttributeValueType>[] => {
    const result: EntityAttributeParam<EntityAttributeValueType>[] = [];

    if (entityClasses) {
        const filterableAttributeTypes = [EntityAttributeType.NUMBER, EntityAttributeType.STRING, EntityAttributeType.BOOLEAN];

        // Get the entityClasses that we need to include in the result
        const filteredEntityClasses = selectedEntityClasses && selectedEntityClasses.length > 0
            ? entityClasses.filter((entityClass) => {                               // Use only the entityClasses selected
                return selectedEntityClasses.includes(entityClass.entityClass);
            })
            : entityClasses;                                                        // Use all the entityClasses

        // Now get all the attributes from all the entityClasses that have not been filtered out.
        filteredEntityClasses.forEach((entityClass) => {
            entityClass.attributeSchema?.forEach((attributeConfig) => {
                // If the attribute type if not 1 of the attribute types that can be use for filtering then skip it
                if (!filterableAttributeTypes.includes(attributeConfig.type)) {
                    return;
                }

                // Look for an existing attribute from the result that is similar to the current attribute config.
                const isExisted = result.find((existingAttribute) => {
                    return compareAttributes(existingAttribute, attributeConfig);
                }) !== undefined;

                // If we don't find an attribute that is similar to the current one, add the current attribute to the result.
                if (!isExisted) {
                    result.push(attributeConfig);
                }
            });
        });
    }

    // Sort the attributes by their labels
    result.sort((attribute1, attribute2) => {
        if (attribute1.label < attribute2.label) {
            return -1;
        }
        if (attribute1.label > attribute2.label) {
            return 1;
        }
        return 0;
    });

    return result;
};


/**
 * When applying entity filter by attributes, the user needs to choose comparison operator. However, not all comparison operator can be
 * used for all attributes. Which operator the user can choose will depends on the attribute's value type. For example, for NUMERIC attribute
 * values, it makes sense to allow the user to choose <, <=, >, >= operator but it doesn't make sense to choose == operator because == is used
 * for substring match. And for attribute value of type STRING, it doesn't make sense to use these operators <, <=, >, >=.
 *
 * Therefore, this is a map of operators that can be used based on attribute value type.
 */
export const SUPPORTED_OPERATORS_BY_ATTRIBUTE_TYPE = {
    [EntityAttributeType.NUMBER]: [
        AttributeValueCompareOperator.LESS_THAN,
        AttributeValueCompareOperator.LESS_THAN_OR_EQUAL,
        AttributeValueCompareOperator.EXACT_EQUAL,
        AttributeValueCompareOperator.GREATER_THAN_OR_EQUAL,
        AttributeValueCompareOperator.GREATER_THAN,
        AttributeValueCompareOperator.EXACT_INCLUDE,
    ],
    [EntityAttributeType.BOOLEAN]: [
        AttributeValueCompareOperator.EXACT_EQUAL,
    ],
    [EntityAttributeType.STRING]: [
        AttributeValueCompareOperator.EQUAL,
        AttributeValueCompareOperator.EXACT_EQUAL,
        AttributeValueCompareOperator.INCLUDE,
        AttributeValueCompareOperator.EXACT_INCLUDE,
    ],
};



// The list of operators that support array of values. This is just for convenience. It make it easier to check if a
// given operator supports multiple values.
export const ARRAY_SUPPORTED_OPERATORS = [
    AttributeValueCompareOperator.INCLUDE,
    AttributeValueCompareOperator.EXACT_INCLUDE,
];



/**
 * Get the user friendly name for NUMERIC/BOOLEAN attribute compare operators
 */
export const getNumericAttributeCompareOperatorDisplayName = (operator: AttributeValueCompareOperator): string => {
    const names = Object.freeze({
        [AttributeValueCompareOperator.LESS_THAN]            : 'entity.attribute.numeric_compare_operator.<',
        [AttributeValueCompareOperator.LESS_THAN_OR_EQUAL]   : 'entity.attribute.numeric_compare_operator.<=',
        [AttributeValueCompareOperator.GREATER_THAN]         : 'entity.attribute.numeric_compare_operator.>',
        [AttributeValueCompareOperator.GREATER_THAN_OR_EQUAL]: 'entity.attribute.numeric_compare_operator.>=',
        [AttributeValueCompareOperator.EXACT_EQUAL]          : 'entity.attribute.numeric_compare_operator.===',
        [AttributeValueCompareOperator.EXACT_INCLUDE]        : 'entity.attribute.numeric_compare_operator.~==',
    });

    return names[operator];
};


/**
 * Get the user friendly name for STRING attribute compare operators. Even though some of the operators are the same for NUMERIC and STRING
 * operator, their translation are different so that is why we need a different function.
 */
export const getStringAttributeCompareOperatorDisplayName = (operator: AttributeValueCompareOperator): string => {
    const names = Object.freeze({
        [AttributeValueCompareOperator.EQUAL]        : 'entity.attribute.string_compare_operator.==',
        [AttributeValueCompareOperator.EXACT_EQUAL]  : 'entity.attribute.string_compare_operator.===',
        [AttributeValueCompareOperator.INCLUDE]      : 'entity.attribute.string_compare_operator.~=',
        [AttributeValueCompareOperator.EXACT_INCLUDE]: 'entity.attribute.string_compare_operator.~==',
    });

    return names[operator];
};



/**
 * Interface for storing ONE attribute condition.
 * Each condition is consists of the attribute, the operator, and 1 or more values to match the attribute values. Here are some examples conditions
 *
 *      type === ['car', 'truck']       // Filter entities which has attribute 'type' exactly equal 'car' or 'truck'
 *      make === ['toyota', 'honda']    // Filter entities which has attribute 'make' exactly equal 'toyota' or 'honda'
 *      year === 2020                   // Filter entities which has attribute 'year' equal 2020
 */
export interface AttributesQueryCondition {
    readonly attribute: EntityAttributeParam<EntityAttributeValueType> | undefined;
    readonly operator: AttributeValueCompareOperator | undefined;
    readonly values: NonEmptyArray<EntityAttributeValueType | undefined>;
}


/**
 * Interface for storing the Attributes Query. This object will contains all the conditions that need to be used in the filters.
 * A query will contain an ARRAY of conditions and a conjunction that specify how to join the conditions.
 */
export interface AttributesQuery {
    readonly conjunction: AttributeConditionConjunction;
    readonly conditions: readonly AttributesQueryCondition[];
}


/**
 * Helper function to Convert 1 single value to string based on the attribute's value type. For NUMBER and BOOLEAN types, we will cast the value
 * to string. For STRING type, we will convert the value to string as well BUT we will also wrap the string inside quotes. For example
 *      value=123 => '123'
 *      value=true => 'true'
 *      value="joe" => '"joe"'
 */
const convertAttributesQueryConditionValueToString = (type: EntityAttributeType, value: EntityAttributeValueType | undefined): string => {
    if (value === undefined) {
        return '""';
    }
    if (type === EntityAttributeType.BOOLEAN) {
        return Boolean(value).toString();
    }
    if (type === EntityAttributeType.NUMBER) {
        return Number(value).toString();
    }
    return `"${value.toString()}"`;    // wrapped the value with quotes e.g. "value"
};



/**
 * Helper function to convert an array of condition values to string. This function will handle BOTH conditions that have 1 value and also
 * conditions that have multiple values. This function will check the OPERATOR and if the operator is one of the operators that support multiple
 * values e.g. "~=", "~==", it will convert all the values in the values array to string and then wrap them inside a [ ]. If the operator is not
 * one of those operators then this function will just return the string form of the first value in the values array. For example
 *      Operator="~==", values=[1, 2, 3]            => '[1|2|3]`
 *      Operator="~=", values=["joe","john","jim"]  => '["joe","john","jim"]`
 *      Operator="<=", value=[3]                    => '3'
 *      Operator="===", value=["joe"]               => '"joe"'
 */
const convertAttributesQueryConditionValuesToString = (condition: AttributesQueryCondition): string => {
    const { attribute, operator, values } = condition;
    if (attribute === undefined || operator === undefined) {
        return '';
    }

    if (ARRAY_SUPPORTED_OPERATORS.includes(operator)) {
        // Convert each value to string
        const stringifiedValues = values.map((value) => {
            return convertAttributesQueryConditionValueToString(attribute.type, value);
        });

        // Join the values using | as separator and wrap the result inside [ ]
        return `[${stringifiedValues.join('|')}]`;
    }

    // Only need to convert 1 value
    return convertAttributesQueryConditionValueToString(attribute.type, values[0]);
};


/**
 * Convert an attributesQuery object into a string format that the backend understand. The result string will be something like these
 *
 *      'AND(make==="toyota",model~==["camry"|"corolla"|"prius"],year===[2020,2021])'
 *      'OR(make==="toyota",model==="camry",year===[2020,2021])'
 */
export const convertAttributesQueryToString = (attributesQuery: AttributesQuery | undefined): string | undefined => {
    if (attributesQuery && attributesQuery.conditions.length > 0) {

        // Conditions that uses ARRAY operators are REQUIRED to contain multiple values in the backend. We can't use ~= or ~== operators
        // and only have 1 value in the array. For example
        //      name~=["joe"]       // This is not allowed
        //      year~==[2020]       // This is not allowed
        //
        // We need to fix those conditions to use the == and === instead like this
        //      name=="joe"         // This is allowed
        //      year===2020         // This is allowed
        const fixedConditions = attributesQuery.conditions.map((condition) => {
            if (condition.operator && ARRAY_SUPPORTED_OPERATORS.includes(condition.operator) && condition.values.length <= 1) {
                // this condition need to be fixed
                if (condition.operator === AttributeValueCompareOperator.INCLUDE) {
                    // Copy the condition and change operator from ~= to ==
                    return {
                        ...condition,
                        operator: AttributeValueCompareOperator.EQUAL,
                    };
                }
                if (condition.operator === AttributeValueCompareOperator.EXACT_INCLUDE) {
                    // Copy the condition and change operator ~== to ===
                    return {
                        ...condition,
                        operator: AttributeValueCompareOperator.EXACT_EQUAL,
                    };
                }
            }
            return condition;
        });

        // stringify each fixedConditions. Each condition will have a "attribute name" + "operator" + value/s. For example
        // 'name==="joe"'
        // 'year~==[2000,2001,2002]'
        const stringifiedConditions = fixedConditions.map((condition) => {
            return `${condition.attribute?.key}${condition.operator}${convertAttributesQueryConditionValuesToString(condition)}`;
        });

        // Join the stringified condition strings using , as separator and wrap the result inside a "AND(...)" or "OR(...)"
        if (attributesQuery.conjunction === AttributeConditionConjunction.OR) {
            return `OR(${stringifiedConditions.join(',')})`;
        }
        return `AND(${stringifiedConditions.join(',')})`;
    }
    return undefined;
};



/**
 * Fetch all entities from the list of entityClasses. This function use the "getEntities" call which doesn't return all entities, it returns
 * 1 pages of entities at a time. Therefore, we need to use an infinite loop to fetch the entities.
 * NOTE: This function will fetch at most 1000 entities. If we need to fetch more entities then need to update this function.
 */
export const getMultipleEntityClassesEntities = async (
    projectId: number,
    homeId: number | undefined,
    entityClasses: readonly string[],
    appDataDispatch: ExtDispatch<UserAppDataStateAction>,
): Promise<readonly Entity[]> => {

    // If there is no entityClasses then return an empty list of entities, don't need to make any API call.
    if (entityClasses.length === 0) {
        return [];
    }

    const result: Entity[] = [];
    const entityClassesString = entityClasses.join(',');    // Backend expects entityClasses as a comas separated string
    const limit = 100;
    let skip = 0;

    // We will use this counter to prevent infinite loop. If this counter goes over 10, we probably should stop.
    // Since we fetch 100 entities per call, calling this 100 times will return 1000 entities which doesn't seem normal.
    let loopCount = 0;
    const maxLoopCount = 10;

    // eslint-disable-next-line no-constant-condition
    while (loopCount < maxLoopCount) {
        // eslint-disable-next-line no-await-in-loop
        const response = await AppAPI.getInstance().getEntities(projectId, { homeId, entityClasses: entityClassesString, skip, limit });
        result.push(...response.items);

        // If the number of items returned from backend is less than the # of items re requested then we can assume we have reached the end and
        // can stop fetching.
        // Or if the "end" index returned from backend passed the "total" count then we can also stop.
        if (response.items.length < limit || (response.range && response.range?.end >= response.range?.total - 1)) {
            break;
        }

        skip += limit;
        loopCount += 1;
    }

    // Add the entities we fetched into the data state
    const batchedSetEntityActions: SetEntityAction[] = [];
    result.forEach((entity) => {
        batchedSetEntityActions.push(setEntity(projectId, entity));
    });
    appDataDispatch(batchActions(batchedSetEntityActions));

    return result;
};



/**
 * Get entity objects for a given array of entity ids. This function will get entities data from cache if available
 * and will make API call to fetch entities if necessary.
 * NOTE: this function might not return the same number of entities as the number of entityIds. If the function is unable to fetch entity
 * for an entityId, the result will not include the entity object for that entityId. Also, this function might not return the entities
 * in the same order as the entityIds.
 */
export const getMultipleEntities = async (
    projectId: number,
    entityIds: readonly string[],
    appDataState: UserAppDataState,
    appDataDispatch: ExtDispatch<UserAppDataStateAction>,
): Promise<readonly Entity[]> => {
    const result: Entity[] = [];

    const fetchActions: Promise<Entity>[] = [];
    entityIds.forEach((entityId) => {
        // Check the dataState to see if we already have entity object for the entityId
        const entity = selectEntityById(appDataState, projectId, entityId);

        // If we have loaded the entity then add it to the result. If not, create an action to load entity object
        if (entity) {
            result.push(entity);
        } else {
            fetchActions.push(AppAPI.getInstance().getEntityById(projectId, entityId));
        }
    });

    // dispatch all the get entity API calls in 1 action
    const responses = await Promise.allSettled(fetchActions);

    // Once we are done loading entities, add it to the data state to be cached. We don't want to add 1 at a time because that will
    // trigger many dataState change events. We will batch them up and then dispatch them all in 1 action
    const batchedSetEntityActions: SetEntityAction[] = [];
    responses.forEach((singleResponse) => {
        if (singleResponse.status === 'fulfilled') {
            // Add the entity object we just loaded to the result
            result.push(singleResponse.value);

            // Create an action to add this entity to the data state
            batchedSetEntityActions.push(setEntity(projectId, singleResponse.value));
        }
    });
    appDataDispatch(batchActions(batchedSetEntityActions));

    return result;
};



/**
 * Given an entityId, find the entity from the data state or fetch it from back end and return the entity's displayName. If can't find
 * the entity, return the entityId as is.
 */
export const getEntityDisplayName = (
    projectId: number, entityId: string,
    appDataState: UserAppDataState, appDataDispatch: ExtDispatch<UserAppDataStateAction>,
    includeEntityId: boolean = false,
): string => {
    const entity = selectEntityById(appDataState, projectId, entityId);
    if (entity) {
        if (includeEntityId) {
            return `${entity.displaySettings.name} #${entityId}`;
        }
        return entity.displaySettings.name;
    }

    // If entity does not exist in the data state yet, fetch it and add it to the data
    AppAPI.getInstance().getEntityById(projectId, entityId)
        .then((entity) => {
            // Add the entity to the data state so that the next time this function is called, we will get it from the data state
            appDataDispatch(setEntity(projectId, entity));
        }).catch(() => {
            // Maybe the entity no longer exist. Do nothing, just continue and use the entityId as is
        });

    return entityId;
};


/**
 * Get the user friendly name for entity health condition based on the provided alertSeverity
 */
export const getEntityHealthConditionDisplayName = (alertSeverity: AlertSeverity | undefined): string => {
    const names = Object.freeze({
        none                    : 'entity.health.label.condition.healthy',
        [AlertSeverity.HIGH]    : 'entity.health.label.condition.high_severity',
        [AlertSeverity.MODERATE]: 'entity.health.label.condition.moderate_severity',
        [AlertSeverity.LOW]     : 'entity.health.label.condition.low_severity',
    });

    return names[alertSeverity ?? 'none'];
};



/**
 * Given a TimeSpan value e.g. DAY, WEEK, or MONTH, return the initial values for a TimeSpan params
 *
 * Let Now="2022-10-31T08:12:13.545Z"
 * Given timeSpan="DAY" and defaultTimeSpanOffset=0
 * Return: {timeSpan: "DAY", timestamps:["2022-10-31T07:00:00.000Z"]}
 *
 * Given timeSpan="DAY" and defaultTimeSpanOffset=-2
 * Return: {timeSpan: "DAY", timestamps:["2022-10-29T07:00:00.000Z"]}
 */
export const getDataParamsForFixedTimeSpan = (timeSpan: TimeSpan, defaultTimeSpanOffset?: number | undefined): {
    readonly timeSpan: TimeSpan;
    readonly timestamps: ReadonlyNonEmptyArray<number>;
} => {
    const startTime = moment();

    // Add some offset to the startTime based on the 'defaultTimeSpanOffset`.
    const offset = defaultTimeSpanOffset ?? 0;
    const startTimestamp = moment(startTime).add(offset, timeSpan).startOf(timeSpan);

    return {
        timeSpan,
        timestamps: [startTimestamp.valueOf()],
    };
};



/**
 * The set of predefined time ranges.
 * This different between CURRENT_XXX and LAST_XXX is this CURRENT_XXX's time range will always start at the beginning of the time unit and end at the
 * end of the time unit. For example,
 *    CURRENT_HOUR: means the startTime=hh:00 and endTime=hh:59.
 *    CURRENT_DAY: means the startTime=00:00 hour and endTime=23:59 hour
 *    CURRENT_WEEK: means the startTime=SUNDAY and endTime=SATURDAY    // SUNDAY will be the default first day of the week
 *    CURRENT_MONTH: means the startTime=1st and endTime=31st          // For months that has 31 days. endTime can be 30 or 28, or 29 for other months
 *
 * LAST_XXX's time range is always XXX time units ago up till NOW. For example lets say now is "Sun Sep 11 2022 12:40:00" therefore endTime will
 * always going to be "Sun Sep 11 2022 12:40:00".
 *    LAST_HOUR: means startTime="Sun Sep 11 2022 11:40:00"
 *    LAST_DAY: means startTime="Sun Sep 10 2022 12:40:00"
 *    LAST_WEEK: means startTime="Sun Sep 04 2022 12:40:00"
 *    LAST_MONTH: means startTime="Thu Aug 11 2022 12:40:00"
 */
export enum PredefinedTimeRangeType {

    CURRENT_HOUR = 'current_hour',
    CURRENT_DAY = 'current_day',
    CURRENT_WEEK = 'current_week',
    CURRENT_MONTH = 'current_month',
    CURRENT_YEAR = 'current_year',

    LAST_HOUR = 'last_hour',
    LAST_DAY = 'last_day',
    LAST_WEEK = 'last_week',
    LAST_MONTH = 'last_month',
    LAST_YEAR = 'last_year',
}


/**
 * Get the start/end timestamp for the specified timeRangeType and the starting value.
 * For timeRangeType CURRENT_XXX, this function will return the beginning and end of the time unit. For example: if "timeRangeType" is CURRENT_WEEK then
 * this function will return the timestamp at the last Sunday midnight to the coming Sunday midnight.
 * Also for timeRangeType of "CURRENT_XXX", there is an OPTIONAL "startValue" the can be used to customize the "beginning" of the time unit because
 * not all project have the same definition of the "beginning". For example, beginning of the week might now always mean Sunday. Beginning of the
 * year might not always be January. Therefore if "timeRangeType" is "CURRENT_WEEK" and "startValue" is 2 then this function will use "Tuesday" as
 * the beginning of the week so it will return the timestamp of the last Tuesday midnight and the coming Tuesday midnight.
 *
 * As for LAST_XXX time range types, this function will return NOW for the endTime and XXX time ago for the startTime. For example, if
 * timeRangeType === "LAST_HOUR" and now is 4:15pm, this function will returns 3:15 and 4:15 as start/end time.
 */
export const getDataParamsForFixedTimeRange = (timeRangeType: PredefinedTimeRangeType, startValue?: number | undefined): {
    readonly startTime: number;
    readonly endTime: number;
} => {
    const now = moment();
    switch (timeRangeType) {
        case PredefinedTimeRangeType.CURRENT_HOUR:
            // Note: startValue can only be up to 59 minutes max. It doesn't make sense for startValue to be 60 minutes or larger.
            return {
                startTime: moment().startOf('hours').add(Math.min(startValue ?? 0, 59), 'minutes').valueOf(),
                endTime  : moment().endOf('hours').add(Math.min(startValue ?? 0, 59), 'minutes').valueOf(),
            };
        case PredefinedTimeRangeType.CURRENT_DAY:
            // Note: startValue can only be up to 23 hours max. It doesn't make sense for startValue to be 24 hours or larger.
            return {
                startTime: moment().startOf('days').add(Math.min(startValue ?? 0, 23), 'hours').valueOf(),
                endTime  : moment().endOf('days').add(Math.min(startValue ?? 0, 23), 'hours').valueOf(),
            };
        case PredefinedTimeRangeType.CURRENT_WEEK:
            // Note: startValue can only be up to 6 days max.
            return {
                startTime: moment().startOf('weeks').add(Math.min(startValue ?? 0, 6), 'days').valueOf(),
                endTime  : moment().endOf('weeks').add(Math.min(startValue ?? 0, 6), 'days').valueOf(),
            };
        case PredefinedTimeRangeType.CURRENT_MONTH:
            // Note: startValue can only be up to 30 days max.
            return {
                startTime: moment().startOf('months').add(Math.min(startValue ?? 0, 30), 'days').valueOf(),
                endTime  : moment().endOf('months').add(Math.min(startValue ?? 0, 30), 'days').valueOf(),
            };
        case PredefinedTimeRangeType.CURRENT_YEAR:
            // Note: startValue can only be up to 11 months max.
            return {
                startTime: moment().startOf('years').add(Math.min(startValue ?? 0, 11), 'months').valueOf(),
                endTime  : moment().endOf('years').add(Math.min(startValue ?? 0, 11), 'months').valueOf(),
            };
        case PredefinedTimeRangeType.LAST_HOUR:
            return {
                startTime: moment(now).subtract(1, 'hours').valueOf(),
                endTime  : now.valueOf(),
            };
        case PredefinedTimeRangeType.LAST_DAY:
            return {
                startTime: moment(now).subtract(1, 'days').valueOf(),
                endTime  : now.valueOf(),
            };
        case PredefinedTimeRangeType.LAST_WEEK:
            return {
                startTime: moment(now).subtract(1, 'weeks').valueOf(),
                endTime  : now.valueOf(),
            };
        case PredefinedTimeRangeType.LAST_MONTH:
            return {
                startTime: moment(now).subtract(1, 'months').valueOf(),
                endTime  : now.valueOf(),
            };
        default:
            // assume interval is "last_year" since we have already checked all other options
            return {
                startTime: moment(now).subtract(1, 'years').valueOf(),
                endTime  : now.valueOf(),
            };
    }
};



/**
 * Helper function to translate an entity class's displaySettings' attributes that need to be translated.
 * The attributes that need to be translated are 'name' and 'description'
 */
const translateEntityClassDisplaySettings = (
    displaySettings: EntityClass['displaySettings'], trans: TFunction,
): EntityClass['displaySettings'] => {
    return {
        ...displaySettings,
        name       : trans(displaySettings.name),
        description: trans(displaySettings.description),
    };
};



/**
 * Helper function to translate an entity class's array of attributeSchemas' attributes that need to be translated
 * The attributes that need to be translated are 'label', 'description', and option.label
 */
const translateEntityClassAttributeSchemas = (
    attributeSchemas: EntityClass['attributeSchema'], trans: TFunction,
): EntityClass['attributeSchema'] => {
    if (!attributeSchemas) {
        return undefined;
    }

    return attributeSchemas.map((attributeSchema) => {
        // Translate EACH attribute schema
        return {
            ...attributeSchema,
            label      : trans(attributeSchema.label),
            description: attributeSchema.description !== undefined ? trans(attributeSchema.description) : undefined,
            options    : attributeSchema.options?.map((option) => {
                return {
                    ...option,
                    label: trans(option.label),
                };
            }),
        };
    });
};


/**
 * Helper function to translate ONE entity class metricsDefinition' attributes that need to be translated
 * The attributes that need to be translated are 'displayName', 'unit' (for numeric metric), and enumDefinition.name (for tag metric)
 */
const translateEntityClassMetricsDefinition = (metricsDefinition: EntityMetricsDefinition, trans: TFunction): EntityMetricsDefinition => {
    // translate EACH metrics definition
    return {
        ...metricsDefinition,
        displayName   : metricsDefinition.displayName !== undefined ? trans(metricsDefinition.displayName) : undefined,
        numericMetrics: metricsDefinition.numericMetrics?.map((numericMetric) => {
            // translate each numeric metric
            return {
                ...numericMetric,
                displayName: numericMetric.displayName !== undefined ? trans(numericMetric.displayName) : undefined,
                unit       : numericMetric.unit !== undefined ? trans(numericMetric.unit) : undefined,
                evaluator  : numericMetric.evaluator ? {
                    ...numericMetric.evaluator,
                    displayName: trans(numericMetric.evaluator.displayName),
                } : undefined,
            };
        }),
        tagMetrics: metricsDefinition.tagMetrics?.map((tagMetric) => {
            // translate each tag metric
            return {
                ...tagMetric,
                displayName    : tagMetric.displayName !== undefined ? trans(tagMetric.displayName) : undefined,
                enumDefinitions: tagMetric.enumDefinitions?.map((enumDef) => {
                    // translate each enum definition
                    return {
                        ...enumDef,
                        name: trans(enumDef.name),
                    };
                }),
            };
        }),
    };
};


/**
 * Helper function to translate an entity class's array of metricsDefinitions' attributes that need to be translated
 */
const translateEntityClassMetricsDefinitions = (
    metricsDefinitions: EntityClass['metricsDefinitions'], trans: TFunction,
): EntityClass['metricsDefinitions'] => {
    if (!metricsDefinitions) {
        return undefined;
    }

    return metricsDefinitions.map((metricsDefinition) => {
        return translateEntityClassMetricsDefinition(metricsDefinition, trans);
    });
};


/**
 * Translate ONE entity class. This will translate all the attributes that can be translated e.g. displayName, description, label, etc...
 */
export const translateEntityClass = (entityClass: EntityClass, trans: TFunction): EntityClass => {
    return {
        ...entityClass,
        displaySettings   : translateEntityClassDisplaySettings(entityClass.displaySettings, trans),
        attributeSchema   : translateEntityClassAttributeSchemas(entityClass.attributeSchema, trans),
        metricsDefinitions: translateEntityClassMetricsDefinitions(entityClass.metricsDefinitions, trans),
    };
};


/**
 * Translate an array of entity classes. This will translate all the attributes that can be translated e.g. displayName, description, label, etc...
 */
export const translateEntityClasses = (entityClasses: readonly EntityClass[] | undefined, trans: TFunction): readonly EntityClass[] | undefined => {
    if (!entityClasses) {
        return undefined;
    }

    return entityClasses?.map((entityClass) => {
        return translateEntityClass(entityClass, trans);
    });
};


/**
 * Custom hook to fetch entity classes and translate them and return the translated entity classes.
 */
export const useSelectTranslatedEntityClasses = (
    projectId: number | undefined, trans: TFunction,
    fetchLatest?: boolean | undefined, showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly EntityClass[] | undefined => {
    // Call the custom hook that fetch entity classes
    const originalEntityClasses = useSelectEntityClasses(projectId, fetchLatest, showLoading, suppressErrors);

    // Translate the original entity classes
    const translatedEntityClasses = useMemo(() => {
        return translateEntityClasses(originalEntityClasses, trans);
    }, [originalEntityClasses, trans]);

    return translatedEntityClasses;
};



/**
 * How the event card should be highlighted based on it's state
 */
export enum CardHighlightStyle {
    BAR = 'bar',                // A bar is drawn under the card using the color based on the state
    BORDER = 'border',          // A border is displayed around the card using the color based on the state
    BACKGROUND = 'background',  // The card's background will be set using the color based on the state
}


/**
 * Type guard function to check if a style value is a CardHighlightStyle
 */
export const isCardHighlightStyle = (
    style: any,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): style is CardHighlightStyle => {
    if (!isValidSettingStringValue(style, true, false, Object.values(CardHighlightStyle))) {
        return errorLogger?.logError({
            message: `Value must be one of the following [${
                Object.values(CardHighlightStyle).map((style) => {
                    return `"${style}"`;
                }).join(', ')
            }]`,
            value: style,
            path : [...(currentConfigPath ?? [])],
        }) ?? false;
    }
    return true;
};


/**
 * Given a "entitySortFieldInfo" that is a combination of sortFieldType and sortFieldName, split the "entitySortFieldInfo" into 2 separate
 * values and return an object containing the individual values, "sortFieldType" and "sortField". However, for "sortFieldType", only return
 * a value if the "sortFieldType" is NOT the EntitySortFieldType.BASIC. If the value is "EntitySortFieldType.BASIC", return undefined for the
 * "sortFieldType" because the back end doesn't need it. For example:
 *
 *      Give: "basic:entityId"
 *      Return: {sortFieldType: undefined, sortField:"entityId"}
 *
 *      Give: "basic:homeId"
 *      Return: {sortFieldType: undefined, sortField:"homeId"}
 *
 *      Give: "attributes:serial_number"
 *      Return: {sortFieldType: "attributes", sortField:"serial_number"}
 *
 *      Give: "descendantCounts:driver"
 *      Return: {sortFieldType: "descendantCounts", sortField:"driver"}
 */
export const splitEntitySortFieldInfo = (entitySortFieldInfo: string | undefined): {
    readonly sortFieldType: EntitySortFieldType.ATTRIBUTES | EntitySortFieldType.DESCENDANT_COUNTS | undefined;
    readonly sortField: string | undefined;
} => {
    // Because the "entitySortFieldInfo" is a combination of sortFieldType and sortField, we need to split it
    const [sortFieldType, sortFieldName] = entitySortFieldInfo ? entitySortFieldInfo.split(':') : [undefined, undefined];

    return {
        sortFieldType: sortFieldType === EntitySortFieldType.ATTRIBUTES || sortFieldType === EntitySortFieldType.DESCENDANT_COUNTS ? sortFieldType : undefined,
        sortField    : sortFieldName,
    };
};
